// v2 banana — a real banana clump: a stout two-section pseudostem of layered
// sheath greens whose big paddle leaves arch up and break over (two chained
// blade segments each, one old leaf yellowing and sagging), a hanging fruit
// stalk carrying a cluster of green-yellow bananas tipped by the dangling purple
// flower bud, and two young offshoot suckers sprouting from the base with their
// own first leaves. Variants re-deal the leaf fan and move the suckers around the
// clump. Ported 1:1 from age-of-mcp's v2/tree-banana.ts, using the library's
// shared `tropic` (blade, h01, lerp, tube) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part } from "../../lib";

const SHEATH = "#7a9a4a";
const SHEATH_2 = "#8fae54";
const LEAF = "#4f9e3c";
const LEAF_2 = "#62b84a";
const LEAF_DEEP = "#3e8531";
const LEAF_OLD = "#a3a542";
const STALK = "#6b7a35";
const FRUIT = "#cfc35a";
const BUD = "#7a3b5e";

function bananaParts(v: number): Part[] {
  const seed = v * 17 + 5;
  const yaw = v * 2.1;
  const parts: Part[] = [
    // layered pseudostem: a stout lower sheath and a slimmer upper one
    tropic.tube([0, 0, 0], [0, 0.42, 0], 0.14, 0.105, SHEATH, 7),
    tropic.tube([0, 0.4, 0], [0.02, 0.74, 0.01], 0.105, 0.075, SHEATH_2, 7),
  ];

  // Five broad paddle leaves arching from the crown, breaking over.
  const N = 5;
  for (let i = 0; i < N; i++) {
    const a = yaw + (i / N) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.6;
    parts.push(...tropic.blade([0.02, 0.78, 0.01], a, {
      segs: 2, len: tropic.lerp(0.72, 0.86, tropic.h01(seed, 20 + i)), width: 0.3,
      thick: 0.04, rise: tropic.lerp(0.3, 0.65, tropic.h01(seed, 30 + i)), droop: 0.8,
      taper: 0.85,
      colors: i % 2 ? [LEAF, LEAF_DEEP] : [LEAF_2, LEAF],
    }));
  }
  // One old, yellowing leaf sagging low against the stem.
  parts.push(...tropic.blade([0.02, 0.74, 0.01], yaw + 2.6, {
    segs: 2, len: 0.6, width: 0.24, thick: 0.035,
    rise: -0.45, droop: 0.3, taper: 0.8, colors: [LEAF_OLD, LEAF_OLD],
  }));

  // The fruit stalk arcs out and down from the crown…
  const ba = yaw + 0.9;
  const sx = Math.cos(ba) * 0.2, sz = Math.sin(ba) * 0.2;
  parts.push(tropic.tube([0.02, 0.72, 0.01], [sx, 0.54, sz], 0.026, 0.02, STALK, 5));
  // …carrying a cluster of fat green-yellow bananas…
  const hands: ReadonlyArray<readonly [number, number, number]> = [
    [0.045, 0.0, 0.0], [-0.03, 0.02, 0.035], [-0.01, -0.05, -0.035],
  ];
  for (const [hx, hy, hz] of hands) {
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.055, 0),
      color: FRUIT, position: [sx + hx, 0.49 + hy, sz + hz],
      scale: [1, 1.45, 1] });
  }
  // …tipped by the dangling purple flower bud.
  parts.push({ geometry: new THREE.ConeGeometry(0.05, 0.14, 5), color: BUD,
    position: [sx, 0.36, sz], rotation: [Math.PI, 0, 0] });

  // Two young suckers leaning out of the clump's foot.
  for (let i = 0; i < 2; i++) {
    const a = yaw + 2.2 + i * 2.4 + tropic.h01(seed, 40 + i) * 0.7;
    const bx = Math.cos(a) * 0.17, bz = Math.sin(a) * 0.17;
    const h = tropic.lerp(0.26, 0.36, tropic.h01(seed, 50 + i));
    parts.push(tropic.tube([bx, 0, bz], [bx * 1.5, h, bz * 1.5], 0.055, 0.038, SHEATH, 6));
    for (let k = 0; k < 2; k++) {
      parts.push(...tropic.blade([bx * 1.5, h + 0.02, bz * 1.5], a + 0.7 + k * 2.2, {
        segs: 1, len: 0.3, width: 0.14, thick: 0.03,
        rise: 0.75 - k * 0.3, droop: 0, taper: 0.8,
        colors: [k ? LEAF : LEAF_2],
      }));
    }
  }
  return parts;
}

export const treeBananaV2 = defineModel({
  id: "tree-banana-v2",
  batchId: "fable",
  name: "Tree banana (v2)",
  tags: ["scenery","tree","banana","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree banana.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.5,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => bananaParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => bananaParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => bananaParts(2) },
  ],
  animations: [],
});
